import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/apiClient';
import { useToast } from '../context/ToastContext';
import { FiPrinter, FiXCircle, FiPackage, FiMapPin, FiPhone, FiMail, FiCalendar, FiTruck, FiDollarSign, FiX } from 'react-icons/fi';
import '../styles/Admin.css';

const AdminOrders = () => {
    const toast = useToast();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [orderSearchQuery, setOrderSearchQuery] = useState('');
    const [detailsModal, setDetailsModal] = useState({ open: false, order: null });
    const [actionLoading, setActionLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const token = sessionStorage.getItem('adminToken');
        if (!token) {
            navigate('/admin/login');
            return;
        }
        fetchOrders();
    }, [navigate]);

    const fetchOrders = async () => {
        try {
            const token = sessionStorage.getItem('adminToken');
            const response = await apiClient.get('/admin/orders', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setOrders(response.data || []);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching orders:', error);
            setLoading(false);
        }
    };

    const handlePrintLabel = async (orderId, shipmentId) => {
        if (!shipmentId) {
            toast.warning('No shipment ID found. Order might not be synced with Shiprocket.');
            return;
        }
        try {
            const token = sessionStorage.getItem('adminToken');
            const response = await apiClient.post(`/admin/orders/${orderId}/label`,
                { shipmentId },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (response.data.labelUrl) {
                window.open(response.data.labelUrl, '_blank');
            } else {
                toast.warning('Label URL not returned.');
            }
        } catch (error) {
            console.error('Error printing label:', error);
            toast.error('Failed to generate label. Check console for details.');
        }
    };

    const handleCancelOrder = async (orderId) => {
        if (!window.confirm('Are you sure you want to cancel this order? This action cannot be undone.')) {
            return;
        }
        try {
            setActionLoading(true);
            const token = sessionStorage.getItem('adminToken');
            await apiClient.post(`/admin/orders/${orderId}/cancel`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Order cancelled successfully!');
            fetchOrders();
        } catch (error) {
            console.error('Error cancelling order:', error);
            toast.error(error.response?.data?.error || 'Failed to cancel order');
        } finally {
            setActionLoading(false);
        }
    };

    if (loading) return <div className="admin-container">Loading...</div>;

    return (
        <div className="admin-container">
            <div className="admin-header">
                <div className="header-left">
                    <h1>Orders</h1>
                </div>
            </div>

            <div className="product-table-wrapper">
                <div className="table-controls">
                    <div className="search-input-wrapper">
                        <input
                            type="text"
                            placeholder="Search by Order ID, Email, or Name..."
                            value={orderSearchQuery}
                            onChange={(e) => setOrderSearchQuery(e.target.value)}
                        />
                        <button
                            className="btn-secondary"
                            style={{ marginLeft: '0.5rem', padding: '0.6rem 1.2rem' }}
                        >
                            Search
                        </button>
                    </div>
                </div>
                <div className="table-responsive">
                    <table className="modern-table">
                        <thead>
                            <tr>
                                <th>Order ID</th>
                                <th>Customer</th>
                                <th>Status</th>
                                <th>Courier</th>
                                <th style={{ textAlign: 'center' }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {orders
                                .filter(order => {
                                    if (orderSearchQuery === '') return true;
                                    const query = orderSearchQuery.toLowerCase();
                                    const orderId = order.orderId ? order.orderId.toLowerCase() : '';
                                    const userEmail = order.userEmail ? order.userEmail.toLowerCase() : '';
                                    const fullName = (order.shippingAddress && order.shippingAddress.fullName) ? order.shippingAddress.fullName.toLowerCase() : '';

                                    return (
                                        orderId.includes(query) ||
                                        userEmail.includes(query) ||
                                        fullName.includes(query)
                                    );
                                })
                                .map((order) => (
                                    <tr key={order.id}>
                                        <td>
                                            <strong style={{ color: '#1976d2' }}>{order.orderId}</strong>
                                        </td>
                                        <td>
                                            {order.shippingAddress?.fullName}<br />
                                            <small style={{ color: '#666' }}>{order.userEmail}</small>
                                        </td>
                                        <td>
                                            <span className={`status-badge ${order.status}`}>{order.status}</span>
                                        </td>
                                        <td>
                                            {order.shippingDetails ? (
                                                <div>
                                                    <strong>{order.shippingDetails.courier_name}</strong><br />
                                                    <small>AWB: {order.shippingDetails.awb_code}</small>
                                                </div>
                                            ) : (
                                                <span style={{ color: 'orange' }}>Pending</span>
                                            )}
                                        </td>
                                        <td>
                                            <div className="row-actions" style={{ display: 'flex', justifyContent: 'center' }}>
                                                <button
                                                    onClick={() => setDetailsModal({ open: true, order: order })}
                                                    style={{
                                                        padding: '8px 16px',
                                                        background: '#1976d2',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        cursor: 'pointer',
                                                        fontSize: '13px',
                                                        fontWeight: '500'
                                                    }}
                                                >
                                                    View Details
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Order Details Modal */}
            {detailsModal.open && detailsModal.order && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000,
                    padding: '20px'
                }} onClick={() => setDetailsModal({ open: false, order: null })}>
                    <div style={{
                        background: 'white',
                        borderRadius: '12px',
                        width: '100%',
                        maxWidth: '800px',
                        maxHeight: '90vh',
                        overflow: 'auto',
                        boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
                    }} onClick={(e) => e.stopPropagation()}>
                        {/* Modal Header */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '20px 24px',
                            borderBottom: '1px solid #eee',
                            position: 'sticky',
                            top: 0,
                            background: 'white',
                            zIndex: 10
                        }}>
                            <h2 style={{ margin: 0, fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <FiPackage /> Order Details
                            </h2>
                            <button
                                onClick={() => setDetailsModal({ open: false, order: null })}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '8px',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <FiX size={20} />
                            </button>
                        </div>

                        {/* Order Status Banner */}
                        <div style={{
                            padding: '16px 24px',
                            background: detailsModal.order.status === 'cancelled' ? '#ffebee' :
                                detailsModal.order.status === 'delivered' ? '#e8f5e9' :
                                    detailsModal.order.status === 'shipped' ? '#e3f2fd' : '#fff3e0',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px'
                        }}>
                            <span style={{
                                padding: '6px 16px',
                                borderRadius: '20px',
                                fontWeight: '600',
                                fontSize: '0.85rem',
                                textTransform: 'uppercase',
                                background: detailsModal.order.status === 'cancelled' ? '#f44336' :
                                    detailsModal.order.status === 'delivered' ? '#4caf50' :
                                        detailsModal.order.status === 'shipped' ? '#2196f3' : '#ff9800',
                                color: 'white'
                            }}>
                                {detailsModal.order.status}
                            </span>
                            <span style={{ color: '#666' }}>
                                Order ID: <strong>{detailsModal.order.orderId}</strong>
                            </span>
                        </div>

                        {/* Order Info Grid */}
                        <div style={{ padding: '24px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
                            {/* Customer Details */}
                            <div style={{ background: '#f8f9fa', padding: '20px', borderRadius: '8px' }}>
                                <h3 style={{ margin: '0 0 16px 0', fontSize: '1rem', color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <FiMapPin /> Shipping Address
                                </h3>
                                <div style={{ lineHeight: '1.8', color: '#555' }}>
                                    <strong>{detailsModal.order.shippingAddress?.fullName}</strong><br />
                                    {detailsModal.order.shippingAddress?.addressLine1}<br />
                                    {detailsModal.order.shippingAddress?.addressLine2 && <>{detailsModal.order.shippingAddress.addressLine2}<br /></>}
                                    {detailsModal.order.shippingAddress?.city}, {detailsModal.order.shippingAddress?.state} - {detailsModal.order.shippingAddress?.postalCode}<br />
                                    <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <FiPhone size={14} /> {detailsModal.order.shippingAddress?.phone}
                                        </span>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <FiMail size={14} /> {detailsModal.order.userEmail}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Order Summary */}
                            <div style={{ background: '#f8f9fa', padding: '20px', borderRadius: '8px' }}>
                                <h3 style={{ margin: '0 0 16px 0', fontSize: '1rem', color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <FiDollarSign /> Order Summary
                                </h3>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span style={{ color: '#666' }}>Subtotal:</span>
                                        <span>₹{detailsModal.order.totalPrice?.toLocaleString('en-IN')}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span style={{ color: '#666' }}>Shipping:</span>
                                        <span>{detailsModal.order.shippingCost ? `₹${detailsModal.order.shippingCost}` : 'Free'}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: '700', fontSize: '1.1rem', borderTop: '1px solid #ddd', paddingTop: '12px' }}>
                                        <span>Total:</span>
                                        <span style={{ color: '#2e7d32' }}>₹{(detailsModal.order.totalPrice + (detailsModal.order.shippingCost || 0)).toLocaleString('en-IN')}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
                                        <span style={{ color: '#666' }}>Payment:</span>
                                        <span style={{
                                            padding: '2px 8px',
                                            borderRadius: '4px',
                                            background: detailsModal.order.paymentMethod === 'cod' ? '#fff3e0' : '#e8f5e9',
                                            color: detailsModal.order.paymentMethod === 'cod' ? '#e65100' : '#2e7d32',
                                            fontSize: '0.85rem',
                                            fontWeight: '600'
                                        }}>
                                            {detailsModal.order.paymentMethod === 'cod' ? 'COD' : 'Prepaid'}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Shipping Details */}
                            <div style={{ background: '#f8f9fa', padding: '20px', borderRadius: '8px' }}>
                                <h3 style={{ margin: '0 0 16px 0', fontSize: '1rem', color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <FiTruck /> Shipping Details
                                </h3>
                                {detailsModal.order.shippingDetails ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', color: '#555' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span style={{ color: '#666' }}>Courier:</span>
                                            <strong>{detailsModal.order.shippingDetails.courier_name}</strong>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span style={{ color: '#666' }}>AWB Code:</span>
                                            <span style={{ fontFamily: 'monospace', background: '#e3f2fd', padding: '2px 8px', borderRadius: '4px' }}>
                                                {detailsModal.order.shippingDetails.awb_code}
                                            </span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span style={{ color: '#666' }}>Shipment ID:</span>
                                            <span>{detailsModal.order.shippingDetails.shipment_id}</span>
                                        </div>
                                    </div>
                                ) : (
                                    <p style={{ color: '#ff9800', margin: 0 }}>Shipping not assigned yet</p>
                                )}
                            </div>

                            {/* Order Timeline */}
                            <div style={{ background: '#f8f9fa', padding: '20px', borderRadius: '8px' }}>
                                <h3 style={{ margin: '0 0 16px 0', fontSize: '1rem', color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <FiCalendar /> Timeline
                                </h3>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', color: '#555' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span style={{ color: '#666' }}>Order Placed:</span>
                                        <span>{new Date(detailsModal.order.createdAt).toLocaleString('en-IN', {
                                            day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                                        })}</span>
                                    </div>
                                    {detailsModal.order.updatedAt && detailsModal.order.updatedAt !== detailsModal.order.createdAt && (
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span style={{ color: '#666' }}>Last Updated:</span>
                                            <span>{new Date(detailsModal.order.updatedAt).toLocaleString('en-IN', {
                                                day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                                            })}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Order Items */}
                        <div style={{ padding: '0 24px 24px' }}>
                            <h3 style={{ margin: '0 0 16px 0', fontSize: '1rem', color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <FiPackage /> Order Items ({detailsModal.order.items?.length || 0})
                            </h3>
                            <div style={{ background: '#f8f9fa', borderRadius: '8px', overflow: 'hidden' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ background: '#e9ecef' }}>
                                            <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '0.85rem', color: '#555' }}>Product</th>
                                            <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: '0.85rem', color: '#555' }}>Qty</th>
                                            <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '0.85rem', color: '#555' }}>Price</th>
                                            <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '0.85rem', color: '#555' }}>Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {detailsModal.order.items?.map((item, idx) => (
                                            <tr key={idx} style={{ borderTop: '1px solid #e9ecef' }}>
                                                <td style={{ padding: '12px 16px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                        {item.image && (
                                                            <img
                                                                src={item.image}
                                                                alt={item.name}
                                                                style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '6px' }}
                                                            />
                                                        )}
                                                        <div>
                                                            <strong style={{ fontSize: '0.9rem' }}>{item.name}</strong>
                                                            {item.sku && <div style={{ color: '#666', fontSize: '0.8rem' }}>SKU: {item.sku}</div>}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td style={{ padding: '12px 16px', textAlign: 'center' }}>{item.quantity}</td>
                                                <td style={{ padding: '12px 16px', textAlign: 'right' }}>₹{item.price?.toLocaleString('en-IN')}</td>
                                                <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: '600' }}>
                                                    ₹{(item.price * item.quantity)?.toLocaleString('en-IN')}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div style={{
                            padding: '16px 24px',
                            borderTop: '1px solid #eee',
                            display: 'flex',
                            gap: '12px',
                            justifyContent: 'flex-end',
                            position: 'sticky',
                            bottom: 0,
                            background: 'white'
                        }}>
                            {detailsModal.order.shippingDetails?.shipment_id && (
                                <button
                                    onClick={() => handlePrintLabel(detailsModal.order.orderId, detailsModal.order.shippingDetails.shipment_id)}
                                    style={{
                                        padding: '10px 20px',
                                        border: '1px solid #4caf50',
                                        borderRadius: '8px',
                                        background: 'white',
                                        color: '#4caf50',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}
                                >
                                    <FiPrinter /> Print Label
                                </button>
                            )}
                            {detailsModal.order.status !== 'cancelled' && detailsModal.order.status !== 'delivered' && (
                                <button
                                    onClick={() => {
                                        setDetailsModal({ open: false, order: null });
                                        handleCancelOrder(detailsModal.order.orderId);
                                    }}
                                    style={{
                                        padding: '10px 20px',
                                        border: 'none',
                                        borderRadius: '8px',
                                        background: '#dc3545',
                                        color: 'white',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}
                                >
                                    <FiXCircle /> Cancel Order
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminOrders;
